import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './UserProfile.css';

function UserProfile() {
  const { user, subscription, updateUser } = useAuth();
  const [profile, setProfile] = useState({ full_name: '', email: '', phone_number: '', national_id: '' });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [profileMessage, setProfileMessage] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (!user) return;
    setProfile({
      full_name: user.full_name || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      national_id: user.national_id || '',
    });
  }, [user]);

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setSavingProfile(true);
    setProfileMessage(null);
    try {
      const updatedUser = await api.put('/api/users/me', profile);
      updateUser(updatedUser);
      setProfileMessage({ type: 'success', text: 'اطلاعات حساب کاربری ذخیره شد.' });
    } catch (error) {
      setProfileMessage({ type: 'error', text: error.message || 'ذخیره اطلاعات انجام نشد.' });
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordMessage(null);
    if (passwords.new_password !== passwords.confirm_password) {
      setPasswordMessage({ type: 'error', text: 'تکرار رمز عبور با رمز جدید یکسان نیست.' });
      return;
    }
    if (passwords.new_password.length < 8) {
      setPasswordMessage({ type: 'error', text: 'رمز عبور جدید باید حداقل ۸ کاراکتر باشد.' });
      return;
    }

    setSavingPassword(true);
    try {
      const result = await api.put('/api/users/me/password', {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
      setPasswordMessage({ type: 'success', text: result.message || 'رمز عبور تغییر کرد.' });
    } catch (error) {
      setPasswordMessage({ type: 'error', text: error.message || 'تغییر رمز عبور انجام نشد.' });
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <main className="profile-page">
      <header className="profile-page-header">
        <div>
          <span className="profile-eyebrow">مدیریت حساب</span>
          <h1>حساب کاربری</h1>
          <p>اطلاعات هویتی و امنیت حساب خود را از این بخش مدیریت کنید.</p>
        </div>
        <div className="profile-avatar" aria-label="نام کاربر">
          {(user?.full_name || user?.email || '?').charAt(0).toUpperCase()}
        </div>
      </header>

      <div className="profile-grid">
        <section className="profile-card profile-details-card">
          <div className="profile-card-heading">
            <h2>اطلاعات شخصی</h2>
            <p>برای تکمیل فرآیند احراز هویت در آینده، اطلاعات زیر را کامل و دقیق ثبت کنید.</p>
          </div>
          <form onSubmit={handleProfileSubmit} className="profile-form">
            <div className="profile-form-grid">
              <label>
                نام و نام خانوادگی
                <input value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} autoComplete="name" />
              </label>
              <label>
                ایمیل
                <input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} autoComplete="email" required />
              </label>
              <label>
                شماره همراه
                <input type="tel" value={profile.phone_number} onChange={(e) => setProfile({ ...profile, phone_number: e.target.value })} placeholder="۰۹۱۲۱۲۳۴۵۶۷" autoComplete="tel" inputMode="tel" />
              </label>
              <label>
                کد ملی
                <input value={profile.national_id} onChange={(e) => setProfile({ ...profile, national_id: e.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder="۱۰ رقم" inputMode="numeric" maxLength="10" />
              </label>
            </div>
            {profileMessage && <div className={`profile-message ${profileMessage.type}`}>{profileMessage.text}</div>}
            <button className="profile-primary-button" type="submit" disabled={savingProfile}>
              {savingProfile ? 'در حال ذخیره…' : 'ذخیره اطلاعات'}
            </button>
          </form>
        </section>

        <section className="profile-card profile-security-card">
          <div className="profile-card-heading">
            <h2>تغییر رمز عبور</h2>
            <p>برای امنیت بیشتر، ابتدا رمز عبور فعلی خود را وارد کنید.</p>
          </div>
          <form onSubmit={handlePasswordSubmit} className="profile-form">
            <label>
              رمز عبور فعلی
              <input type="password" value={passwords.current_password} onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })} autoComplete="current-password" required />
            </label>
            <label>
              رمز عبور جدید
              <input type="password" value={passwords.new_password} onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })} autoComplete="new-password" minLength="8" required />
            </label>
            <label>
              تکرار رمز عبور جدید
              <input type="password" value={passwords.confirm_password} onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })} autoComplete="new-password" minLength="8" required />
            </label>
            {passwordMessage && <div className={`profile-message ${passwordMessage.type}`}>{passwordMessage.text}</div>}
            <button className="profile-secondary-button" type="submit" disabled={savingPassword}>
              {savingPassword ? 'در حال تغییر…' : 'تغییر رمز عبور'}
            </button>
          </form>
        </section>

        <section className="profile-card profile-subscription-card">
          <div>
            <span className="subscription-label">اشتراک فعلی</span>
            <h2>{subscription?.plan?.name || 'رایگان'}</h2>
            <p>خرید آنلاین اشتراک به‌زودی در این بخش فعال می‌شود.</p>
          </div>
          <button className="profile-purchase-button" type="button" disabled title="خرید اشتراک به‌زودی فعال می‌شود">
            خرید اشتراک — به‌زودی
          </button>
        </section>
      </div>
    </main>
  );
}

export default UserProfile;
